package com.islamicmedia.site.components

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLScriptElement

/**
 * Stripe Elements, bound to a SetupIntent — the card-change flow.
 *
 * WHY THIS EXISTS SEPARATELY FROM THE OFFER PAGE'S STRIPE CODE: the `/register` page mounts the
 * *Checkout Sessions* Elements SDK (`initCheckoutElementsSdk` → `loadActions().confirm()`), which
 * only knows how to complete a purchase. Saving a card without charging it is a different Stripe
 * API entirely: plain `stripe.elements({clientSecret})` over a SetupIntent, confirmed with
 * `confirmSetup`. The two cannot share a mount, so they do not share a file either — but this
 * follows the same rules the offer page learned the hard way.
 *
 * Chief among them: it is RAW JS, not Kotlin `dynamic` calls. Mounting Elements is a promise-heavy
 * API, and driving it through Kotlin/JS interop is how the offer page once lost a whole click
 * handler to a bad `.asDynamic()` with no visible error. Everything below is one injected script,
 * and Kotlin only ever calls named methods on it.
 *
 * Loaded from `js.stripe.com/v3`, not the versioned `dahlia` build the offer page uses. v3 is the
 * long-stable channel and is where `confirmSetup` lives; the pinned build exists for the Checkout
 * SDK's sake and carries its own API version, which this flow does not want.
 */
internal const val CARD_ELEMENT_ID = "ff-card-element"

private const val GLUE_ID = "ff-stripe-card-glue"

/**
 * Inject the glue once. Idempotent by element id, so calling it from both the standalone page and
 * the in-app modal is safe.
 */
fun installStripeCardGlue() {
    if (document.getElementById(GLUE_ID) != null) return
    val script = document.createElement("script") as HTMLScriptElement
    script.id = GLUE_ID
    script.textContent = """
(function(){
  var S = window.ffCard = window.ffCard || {};

  S.loadJs = function(cb){
    if (window.Stripe) { cb(true); return; }
    var existing = document.getElementById('ff-stripe-js-v3');
    if (existing) { existing.addEventListener('load', function(){ cb(!!window.Stripe); }); return; }
    var s = document.createElement('script');
    s.id = 'ff-stripe-js-v3';
    s.src = 'https://js.stripe.com/v3/';
    s.async = true;
    s.onload = function(){ cb(!!window.Stripe); };
    s.onerror = function(){ cb(false); };
    document.head.appendChild(s);
  };

  // Mount the card fields into #nodeId against a SetupIntent client secret.
  S.mount = function(pk, clientSecret, nodeId, cb){
    S.loadJs(function(ok){
      if (!ok) { cb('nojs'); return; }
      try {
        var stripe = window.Stripe(pk);
        S.stripe = stripe;
        // Tear down a previous mount — reopening the sheet must not stack two iframes.
        if (S.element) { try { S.element.unmount(); } catch (e) {} S.element = null; }
        var appearance = {
          variables: {
            colorPrimary: '#0E7C7B',
            colorText: '#171717',
            colorDanger: '#EF4444',
            borderRadius: '12px',
            fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif'
          }
        };
        var elements = stripe.elements({ clientSecret: clientSecret, appearance: appearance });
        S.elements = elements;
        var el = elements.create('payment', { fields: { billingDetails: 'never' } });
        el.mount('#' + nodeId);
        S.element = el;
        // 'ready' rather than resolving immediately: the node exists long before the iframe
        // has painted, and a spinner removed too early shows an empty box.
        el.on('ready', function(){ cb('ok'); });
        el.on('loaderror', function(e){
          cb('err:' + ((e && e.error && e.error.message) ? e.error.message : 'Could not load the card form'));
        });
      } catch (e) { cb('err:' + ((e && e.message) ? e.message : String(e))); }
    });
  };

  // Confirm. redirect:'if_required' keeps the common case on this page — only a card that
  // demands 3-D Secure leaves for the bank and comes back to returnUrl.
  S.confirm = function(returnUrl, cb){
    if (!S.stripe || !S.elements) { cb('nomount'); return; }
    try {
      S.stripe.confirmSetup({
        elements: S.elements,
        confirmParams: { return_url: returnUrl },
        redirect: 'if_required'
      }).then(function(r){
        if (r && r.error) {
          cb('err:' + (r.error.message || 'Could not save your card'));
        } else if (r && r.setupIntent && r.setupIntent.id) {
          cb('ok:' + r.setupIntent.id);
        } else {
          // No error and no intent means Stripe redirected; the return_url handles it.
          cb('redirect');
        }
      }).catch(function(e){ cb('err:' + ((e && e.message) ? e.message : String(e))); });
    } catch (e) { cb('err:' + ((e && e.message) ? e.message : String(e))); }
  };
})();
    """.trimIndent()
    document.head?.appendChild(script)
}

/** Mount the card fields. [onStatus] gets "ok", "nojs", or "err:<message>". */
fun mountCardElement(publishableKey: String, clientSecret: String, onStatus: (String) -> Unit) {
    val ff = window.asDynamic().ffCard
    if (ff == null) { onStatus("nojs"); return }
    ff.mount(publishableKey, clientSecret, CARD_ELEMENT_ID, onStatus)
}

/**
 * Confirm the card. [onStatus] gets "ok:<setupIntentId>", "redirect" (Stripe navigated away for
 * 3-D Secure and will come back to [returnUrl]), "nomount", or "err:<message>".
 */
fun confirmCardSetup(returnUrl: String, onStatus: (String) -> Unit) {
    val ff = window.asDynamic().ffCard
    if (ff == null) { onStatus("nomount"); return }
    ff.confirm(returnUrl, onStatus)
}

/** Turn a glue status string into something worth showing a person. */
fun cardErrorMessage(status: String): String = when {
    status == "nojs" -> "Couldn't reach the payment provider. Check your connection and try again."
    status == "nomount" -> "The card form isn't ready yet. Give it a second and try again."
    status.startsWith("err:") -> status.removePrefix("err:").ifBlank { "Couldn't save your card." }
    else -> "Couldn't save your card."
}
